import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { IUser, IUserData, UserStatus } from 'types/user.types'

import CustomIcon from 'components/icon/CustomIcon'
import CustomText from 'components/text/CustomText'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import { RoomTypes } from 'types/rooms.types'
import { filterUserNameFromRoomName } from 'utils/hooks/useRoom'
import { TITLES } from 'locales/locales'
import { useTranslation } from 'react-i18next'

// Function to get avatar source based on room type and deletion status
const getAvatarSource = (
  isDeleted: boolean | undefined,
  roomType: string,
  otrAvatar: string | undefined,
  roomAvatar: string | undefined,
): string => {
  if (isDeleted) {
    return icons.iconDisabledUser.src
  }

  if (roomType === RoomTypes.OTR) {
    return otrAvatar || ''
  }

  if (roomType === RoomTypes.GM) {
    return icons.groupblueIcon.src || ''
  }
  return roomAvatar || ''
}

// Function to get avatar text based on room type and members
const getAvatarText = (
  roomType: string,
  name: string,
  otrName?: string,
  avatarSource?: string,
): string => {
  if (avatarSource === icons.groupblueIcon.src) {
    return ''
  }

  if (roomType === RoomTypes.DM) {
    return name
  }

  if (roomType === RoomTypes.OTR) {
    if (otrName) {
      return otrName
    }
  }
  return ''
}

interface IRoomProps {
  isSearch: boolean
  isAppear: boolean
  myId: string
  roomId: string
  notificationCount?: number
  users: IUserData
  roomType: string
  members: Array<string>
  currentRoomId: string
  isVanishMode?: boolean
  roomOwner?: IUser
  invitedUsers?: Array<string>
  name?: string;
  onNavigate: (roomId: string) => void
  onHideConversationPanel?: () => void
}

const Room = ({
  isSearch,
  isAppear,
  myId,
  roomId,
  roomType,
  members,
  users,
  currentRoomId,
  notificationCount = 0,
  isVanishMode,
  roomOwner,
  invitedUsers = [],
  name: nameOfRoom,
  onNavigate,
  onHideConversationPanel,
}: IRoomProps) => {
  const isMyRoom = roomOwner?._id === myId
  const [roomInfo, setRoomInfo] = useState<{
    status?: UserStatus
    name?: string
    roomAvatar?: string
    isDeleted?: boolean
    otrName?: string
    otrAvatar?: string
  }>({})

  const { t } = useTranslation()

  const getRoomInfoByMembers = useCallback(() => {
    members = members || []
    users = users || {}

    const membersWithoutMe = isVanishMode
      ? members
      : members.filter((memberId) => memberId !== myId)

    let otrName = ''
    let otrAvatar = ''
    if (isVanishMode) {
      const isSingleMember = members.length === 1
      const isDoubleMemberWithMe = members.length === 2 && members.includes(myId)
      const getDefaultName = () => t(TITLES.DEFAULT_OTR_ROOM_HEADER)
      const getFilteredName = () =>
        filterUserNameFromRoomName(nameOfRoom as string, 'GM', users[myId]?.fullname as string)
    
      if (isSingleMember) {
        if (isMyRoom) {
          otrName = getDefaultName()
          otrAvatar = icons.groupblueIcon.src || ''
        } else {
          otrName = roomOwner?.fullname || ''
          otrAvatar = roomOwner?.avatar || ''
        }
      } else if (isDoubleMemberWithMe) {
        const otherMemberId = members.find((memberId) => memberId !== myId)
        otrName = getFilteredName() || getDefaultName()
        otrAvatar = (otherMemberId && users[otherMemberId]?.avatar) || ''
      } else {
        otrName = getFilteredName() || getDefaultName()
        otrAvatar = icons.groupblueIcon.src || ''
      }
    }
    

    if (membersWithoutMe.length === 0) {
      const { avatar, fullname, status, isDeleted,isOnline }: IUser = users[myId] ?? {}
      const userStatus: UserStatus = isOnline ? status as UserStatus : UserStatus.OFFLINE;
      return { name: fullname, roomAvatar: avatar, status: userStatus, isDeleted }
    } else if (roomType === 'GM' || roomType === 'CM') {
      const name = filterUserNameFromRoomName(
        nameOfRoom as string,
        'GM',
        users[myId]?.fullname as string,
      )
      return { name, roomAvatar: undefined, status: undefined }
    }

    const userId = membersWithoutMe[0]
    const { avatar = '', fullname, status, isDeleted ,isOnline} = users[userId] || {}


    const userStatus: UserStatus = isOnline ? users[userId]?.status ?? status as UserStatus : UserStatus.OFFLINE;

    return {
      name: fullname,
      roomAvatar: avatar,
      status: userStatus,
      isDeleted,
      otrName,
      otrAvatar,
    }
  }, [members, users, isVanishMode, isMyRoom, roomOwner, nameOfRoom])

  const onClick = () => {
    if (currentRoomId !== roomId) {
      onNavigate(roomId)
    }
    onHideConversationPanel?.()
  }

  useEffect(() => {
    const info = getRoomInfoByMembers()
    setRoomInfo(info)
  }, [nameOfRoom, isVanishMode, members, users, myId, users])

  const NotificationView = useMemo(() => {
    return (
      <CountWrapper>
        <CustomText color={colors.vimeDark} typography="body7">
          {notificationCount}
        </CustomText>
      </CountWrapper>
    )
  }, [notificationCount])

  const {
    status = UserStatus.OFFLINE,
    name = '',
    roomAvatar,
    isDeleted,
    otrName = '',
    otrAvatar = '',
  } = roomInfo

  const avatarSource = getAvatarSource(isDeleted, roomType, otrAvatar, roomAvatar)
  const avatarText = getAvatarText(roomType, name, otrName, avatarSource)

  if (!isSearch && !isAppear) return null
  return (
    <Wrapper selectedRoom={currentRoomId === roomId} onClick={onClick}>
      <RoomInfoWrapper>
        {isVanishMode && <CustomIcon source={icons.otrIconOn} />}
        <Avatar
          color={colors.orange}
          source={avatarSource}
          text={name}
          size={AvatarSize.small}
          status={status}
        />
        <StyledText typography="header3" color={colors.white}>
          {isVanishMode ? otrName : name}
        </StyledText>
      </RoomInfoWrapper>
      {!!notificationCount && NotificationView}
    </Wrapper>
  )
}

export default memo(Room)

const StyledText = styled(CustomText)`
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 6px 16px 6px 16px;
  background-color: ${({ selectedRoom }: { selectedRoom: boolean }) =>
    selectedRoom ? 'rgba(231, 245, 251, 0.35)' : colors.vime};
  &:hover {
    background-color: rgba(231, 245, 251, 0.2);
  }
`

const RoomInfoWrapper = styled.div`
  max-width: 180px;
  display: flex;
  align-items: center;
  column-gap: 8px;
`

const CountWrapper = styled.div.attrs((props: { more: boolean }) => props)`
  /* position: absolute; */
  width: fit-content;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 26px;
  min-width: 18px;
  padding: 0 6px;
  height: 18px;
  border-radius: 50px;
  background-color: ${colors.white};
  color: ${colors.white};
`
